import ContactSupportOutlinedIcon from '@mui/icons-material/ContactSupportOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import LogoutIcon from '@mui/icons-material/Logout';
import ManageAccountsOutlinedIcon from '@mui/icons-material/ManageAccountsOutlined';
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined';
import {
  Avatar,
  Badge,
  Box,
  Button,
  Divider,
  MenuItem,
  MenuList,
  Paper,
  Popover,
  Stack,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { useNavigate } from 'react-router';

import { BASE, NESTED_ROUTES } from 'common/src/routes/BaseRoutes';
import { generateNestedRoute } from 'common/src/routes/helpers';

import useAuth from 'portal/src/auth/useAuth';
import { useAlertsBadgeCount } from 'portal/src/hooks/useAlertsBadgeCount';
import { useHasAlertsFeature } from 'portal/src/hooks/useHasAlertsFeature';
import { getNameInitials } from 'portal/src/utils/names';

const UserProfilePopup = () => {
  const navigate = useNavigate();
  const { getUser, getUserPicture, logout } = useAuth();
  const { name, email } = getUser();
  const { count, isError } = useAlertsBadgeCount();
  const hasAlertsFeature = useHasAlertsFeature();
  const picSrc = getUserPicture();

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const supportContactLink = generateNestedRoute(
    BASE.SUPPORT,
    NESTED_ROUTES.SUPPORT.CONTACT_US,
  );

  return (
    <>
      <Button
        onClick={(event) => setAnchorEl(anchorEl ? null : event.currentTarget)}
        sx={{
          borderRadius: 5,
          border: '1px solid',
          borderColor: 'divider',
          ml: 'auto',
          mr: 2,
          p: 0.5,
        }}
      >
        <Badge
          badgeContent={count}
          color="error"
          overlap="circular"
          invisible={!hasAlertsFeature || isError}
          slotProps={{
            badge: {
              sx: { height: 16, minWidth: 17, fontSize: 10, fontWeight: 600 },
            },
          }}
        >
          <Avatar
            src={picSrc}
            sx={{
              height: 34,
              width: 34,
              fontSize: '15px',
              fontWeight: 800,
              bgcolor: picSrc ? 'transparent' : 'secondary.main',
              color: 'secondary.contrastText',
              border: '2px solid',
              borderColor: 'secondary.main',
            }}
          >
            {getNameInitials(name)}
          </Avatar>
        </Badge>
        {anchorEl ? (
          <KeyboardArrowUpIcon
            fontSize="small"
            sx={{ color: 'text.primary', ml: 0.5 }}
          />
        ) : (
          <KeyboardArrowDownIcon
            fontSize="small"
            sx={{ color: 'text.primary', ml: 0.5 }}
          />
        )}
      </Button>
      <Popover
        open={!!anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Paper>
          <Box sx={{ px: 2, py: 1 }}>
            <Stack display="flex">
              <Typography variant="h6">{name}</Typography>
              <Typography variant="body2" color="textSecondary">
                {email}
              </Typography>
            </Stack>
          </Box>
          <Divider />
          <MenuList sx={{ p: 0 }}>
            {hasAlertsFeature && (
              <MenuItem
                tabIndex={0}
                onClick={() => {
                  navigate(BASE.ALERTS);
                  setAnchorEl(null);
                }}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <NotificationsOutlinedIcon fontSize="small" color="inherit" />
                  Alerts
                </Box>
                <Badge
                  badgeContent={count}
                  color="error"
                  invisible={isError}
                  sx={{ mr: 2 }}
                ></Badge>
              </MenuItem>
            )}
            <MenuItem
              tabIndex={0}
              onClick={() => {
                navigate(BASE.USER_SETTINGS);
                setAnchorEl(null);
              }}
            >
              <ManageAccountsOutlinedIcon
                fontSize="small"
                color="inherit"
                sx={{ mr: 0.5 }}
              />
              Profile & Preferences
            </MenuItem>
            <MenuItem
              tabIndex={0}
              onClick={() => {
                navigate(supportContactLink);
                setAnchorEl(null);
              }}
            >
              <ContactSupportOutlinedIcon
                fontSize="small"
                color="inherit"
                sx={{ mr: 0.5 }}
              />
              Support & FAQs
            </MenuItem>
            <MenuItem
              tabIndex={0}
              sx={{ borderTop: '1px solid', borderColor: 'divider', py: 1.5 }}
              onClick={() => {
                logout();
                navigate('/');
              }}
            >
              <LogoutIcon fontSize="small" color="inherit" sx={{ mr: 0.5 }} />
              Logout
            </MenuItem>
          </MenuList>
        </Paper>
      </Popover>
    </>
  );
};

export default UserProfilePopup;
